// ---------------------------------------------------------------------------
// Iconic landmark detection
// ---------------------------------------------------------------------------
// Some places are so significant — Redwoods, Grand Canyon, Mount Washington,
// Yosemite, etc. — that when a route passes through (or right by) them, that
// fact ALWAYS deserves to be the lead reason on the card. The OSM corridor
// scan doesn't reliably capture them as POIs (huge multi-polygons, parent
// relations, varying tagging), so we maintain a small curated registry of
// natural/cultural icons with bounding boxes and name regexes.
//
// Detection is the union of:
//   1. Name match — any name in the route's collected feature arrays
//      (parkNames / natureNames / landmarkNames / historicNames / waterNames)
//      matches an icon's regex.
//   2. Geometry match — the route polyline crosses an icon's bbox.
//
// Both signals are intentionally generous: false positives on iconic places
// are far less harmful than missing one.
// ---------------------------------------------------------------------------

import type { LngLat } from "@/utils/polyline";

export type IconicKind =
  | "forest"     // redwoods, sequoias, giant cedar groves
  | "canyon"    // Grand Canyon, Bryce, Zion-narrows
  | "mountain"  // Mount Washington, Rainier, Hood
  | "park"      // Yosemite Valley, Yellowstone, Glacier NP
  | "coast"     // iconic coastlines (Big Sur, Cape Cod, Acadia shore)
  | "lake"      // Tahoe, Crater Lake
  | "desert"    // Death Valley, Monument Valley
  | "street"    // iconic scenic streets, alleys, boulevards
  | "district"  // historic neighborhoods, waterfronts, plazas
  | "landmark"; // Golden Gate Bridge, Mount Rushmore

export interface IconicLandmark {
  /** Canonical display name shown to users. */
  name: string;
  kind: IconicKind;
  /** Regex tested against name strings the corridor scan returned. */
  match: RegExp;
  /** Optional [minLng, minLat, maxLng, maxLat] — when present, route polylines
   *  crossing this box also trigger the call-out, even without a name match. */
  bbox?: [number, number, number, number];
  /** Verb used in the why-line: "through", "along", "by", "across". */
  preposition?: "through" | "along" | "by" | "across";
}

// Curated registry. Bounding boxes are deliberately loose — they target the
// "you're in/near it" experience, not strict park boundaries.
export const ICONIC_LANDMARKS: IconicLandmark[] = [
  // ---------- Forests ----------
  {
    name: "the Redwoods",
    kind: "forest",
    match: /\b(redwood|sequoia|muir woods|avenue of the giants|jedediah smith|prairie creek|humboldt redwoods)\b/i,
    bbox: [-124.4, 38.0, -123.5, 41.8],
    preposition: "through",
  },
  {
    name: "Sequoia National Park",
    kind: "forest",
    match: /\b(giant forest|sequoia national park|kings canyon)\b/i,
    bbox: [-118.95, 36.3, -118.4, 36.95],
    preposition: "through",
  },
  // ---------- Canyons ----------
  {
    name: "the Grand Canyon",
    kind: "canyon",
    match: /\bgrand canyon\b/i,
    bbox: [-113.1, 35.85, -111.7, 36.55],
    preposition: "by",
  },
  {
    name: "Zion National Park",
    kind: "canyon",
    match: /\bzion( national park)?\b/i,
    bbox: [-113.2, 37.18, -112.85, 37.45],
    preposition: "through",
  },
  {
    name: "Bryce Canyon",
    kind: "canyon",
    match: /\bbryce canyon\b/i,
    bbox: [-112.3, 37.55, -112.1, 37.7],
    preposition: "by",
  },
  // ---------- Mountains ----------
  {
    name: "Mount Washington",
    kind: "mountain",
    match: /\bmount washington\b/i,
    bbox: [-71.35, 44.22, -71.2, 44.32],
    preposition: "by",
  },
  {
    name: "Mount Rainier",
    kind: "mountain",
    match: /\b(mount|mt\.?) rainier\b/i,
    bbox: [-122.1, 46.7, -121.4, 47.05],
    preposition: "by",
  },
  {
    name: "Mount Hood",
    kind: "mountain",
    match: /\b(mount|mt\.?) hood\b/i,
    bbox: [-121.85, 45.25, -121.55, 45.45],
    preposition: "by",
  },
  {
    name: "the Tetons",
    kind: "mountain",
    match: /\b(grand teton|teton range|tetons)\b/i,
    bbox: [-110.95, 43.55, -110.5, 44.0],
    preposition: "through",
  },
  // ---------- Parks ----------
  {
    name: "Yosemite National Park",
    kind: "park",
    match: /\byosemite\b/i,
    bbox: [-119.9, 37.5, -119.2, 38.18],
    preposition: "through",
  },
  {
    name: "Yellowstone National Park",
    kind: "park",
    match: /\byellowstone\b/i,
    bbox: [-111.2, 44.13, -109.8, 45.12],
    preposition: "through",
  },
  {
    name: "Glacier National Park",
    kind: "park",
    match: /\bglacier national park\b/i,
    bbox: [-114.5, 48.2, -113.2, 49.0],
    preposition: "through",
  },
  {
    name: "Acadia National Park",
    kind: "park",
    match: /\bacadia\b/i,
    bbox: [-68.4, 44.25, -68.1, 44.42],
    preposition: "through",
  },
  {
    name: "the Great Smoky Mountains",
    kind: "park",
    match: /\b(great smoky|smoky mountains)\b/i,
    bbox: [-84.0, 35.4, -83.0, 35.85],
    preposition: "through",
  },
  // ---------- Coast ----------
  {
    name: "Big Sur",
    kind: "coast",
    match: /\bbig sur\b/i,
    bbox: [-121.95, 35.85, -121.4, 36.45],
    preposition: "along",
  },
  {
    name: "Cape Cod",
    kind: "coast",
    match: /\bcape cod\b/i,
    bbox: [-70.6, 41.5, -69.9, 42.1],
    preposition: "along",
  },
  // ---------- Lakes ----------
  {
    name: "Lake Tahoe",
    kind: "lake",
    match: /\blake tahoe\b/i,
    bbox: [-120.25, 38.85, -119.85, 39.28],
    preposition: "by",
  },
  {
    name: "Crater Lake",
    kind: "lake",
    match: /\bcrater lake\b/i,
    bbox: [-122.25, 42.85, -122.0, 43.05],
    preposition: "by",
  },
  // ---------- Desert ----------
  {
    name: "Death Valley",
    kind: "desert",
    match: /\bdeath valley\b/i,
    bbox: [-117.5, 35.7, -116.3, 37.1],
    preposition: "through",
  },
  {
    name: "Monument Valley",
    kind: "desert",
    match: /\bmonument valley\b/i,
    bbox: [-110.3, 36.85, -110.0, 37.1],
    preposition: "through",
  },
  // ---------- Landmarks ----------
  {
    name: "the Golden Gate Bridge",
    kind: "landmark",
    match: /\bgolden gate bridge\b/i,
    bbox: [-122.485, 37.805, -122.465, 37.835],
    preposition: "across",
  },
  {
    name: "Mount Rushmore",
    kind: "landmark",
    match: /\bmount rushmore\b/i,
    bbox: [-103.48, 43.85, -103.4, 43.9],
    preposition: "by",
  },
  {
    name: "Hoover Dam",
    kind: "landmark",
    match: /\bhoover dam\b/i,
    bbox: [-114.75, 36.0, -114.7, 36.04],
    preposition: "across",
  },

  // ---------- Iconic streets (curated, high-rated) ----------
  // Tight bboxes — match a route that drives on (or right next to) the named
  // street. Name regex catches OSM-tagged hits even when geometry is offset.
  { name: "Jones Street", kind: "street", match: /\bjones street\b/i, bbox: [-81.100, 32.069, -81.085, 32.082], preposition: "along" },
  { name: "Rainbow Row", kind: "street", match: /\brainbow row\b/i, bbox: [-79.930, 32.774, -79.924, 32.778], preposition: "along" },
  { name: "Acorn Street", kind: "street", match: /\bacorn street\b/i, bbox: [-71.070, 42.357, -71.064, 42.360], preposition: "along" },
  { name: "Lombard Street", kind: "street", match: /\blombard street\b/i, bbox: [-122.422, 37.800, -122.415, 37.804], preposition: "along" },
  { name: "St. Charles Avenue", kind: "street", match: /\bst\.?\s*charles (avenue|ave)\b/i, bbox: [-90.135, 29.918, -90.070, 29.962], preposition: "along" },
  { name: "Elfreth's Alley", kind: "street", match: /\belfreth'?s alley\b/i, bbox: [-75.144, 39.952, -75.140, 39.954], preposition: "along" },
  { name: "Monument Avenue", kind: "street", match: /\bmonument (avenue|ave)\b/i, bbox: [-77.495, 37.553, -77.450, 37.558], preposition: "along" },
  { name: "Commonwealth Avenue", kind: "street", match: /\bcommonwealth (avenue|ave)\b/i, bbox: [-71.115, 42.345, -71.075, 42.358], preposition: "along" },
  { name: "Eastern Parkway", kind: "street", match: /\beastern parkway\b/i, bbox: [-73.965, 40.668, -73.928, 40.675], preposition: "along" },
  { name: "Summit Avenue", kind: "street", match: /\bsummit (avenue|ave)\b/i, bbox: [-93.175, 44.934, -93.100, 44.946], preposition: "along" },
  { name: "Sheridan Road", kind: "street", match: /\bsheridan (road|rd)\b/i, bbox: [-87.685, 41.950, -87.640, 42.205], preposition: "along" },
  { name: "Chestnut Street", kind: "street", match: /\bchestnut street\b/i, bbox: [-75.205, 39.948, -75.140, 39.953], preposition: "along" },
  { name: "Princess Anne Street", kind: "street", match: /\bprincess anne street\b/i, bbox: [-77.466, 38.295, -77.456, 38.312], preposition: "along" },
  { name: "Grove Court", kind: "street", match: /\bgrove court\b/i, bbox: [-74.007, 40.732, -74.003, 40.734], preposition: "along" },
  { name: "King Street", kind: "street", match: /\bking street\b/i, bbox: [-79.940, 32.770, -79.928, 32.795], preposition: "along" },
  { name: "Magazine Street", kind: "street", match: /\bmagazine street\b/i, bbox: [-90.105, 29.920, -90.060, 29.952], preposition: "along" },
  { name: "Palm Canyon Drive", kind: "street", match: /\bpalm canyon (drive|dr)\b/i, bbox: [-116.555, 33.780, -116.538, 33.840], preposition: "along" },
  { name: "Lake Shore Drive", kind: "street", match: /\blake shore (drive|dr)\b/i, bbox: [-87.640, 41.780, -87.605, 42.005], preposition: "along" },
  { name: "Scenic Highway 30A", kind: "street", match: /\b(scenic )?(highway )?30a\b/i, bbox: [-86.300, 30.295, -85.985, 30.365], preposition: "along" },
  { name: "Ocean Drive", kind: "street", match: /\bocean (drive|dr)\b/i, bbox: [-80.137, 25.770, -80.123, 25.795], preposition: "along" },
  { name: "East Bay Street", kind: "street", match: /\beast bay street\b/i, bbox: [-79.932, 32.770, -79.923, 32.792], preposition: "along" },

  // ---------- Iconic landmarks — North America ----------
  { name: "Central Park", kind: "park", match: /\bcentral park\b/i, bbox: [-73.982, 40.764, -73.949, 40.800], preposition: "by" },
  { name: "the Brooklyn Bridge", kind: "landmark", match: /\bbrooklyn bridge\b/i, bbox: [-74.006, 40.702, -73.991, 40.714], preposition: "across" },
  { name: "the High Line", kind: "landmark", match: /\bhigh line\b/i, bbox: [-74.012, 40.740, -73.998, 40.758], preposition: "by" },
  { name: "Millennium Park", kind: "park", match: /\bmillennium park\b/i, bbox: [-87.626, 41.881, -87.620, 41.884], preposition: "by" },
  { name: "Balboa Park", kind: "park", match: /\bbalboa park\b/i, bbox: [-117.158, 32.724, -117.128, 32.748], preposition: "through" },
  { name: "Griffith Observatory", kind: "landmark", match: /\bgriffith observatory\b/i, bbox: [-118.303, 34.116, -118.297, 34.121], preposition: "by" },
  { name: "Pike Place Market", kind: "landmark", match: /\bpike place( market)?\b/i, bbox: [-122.346, 47.607, -122.339, 47.612], preposition: "by" },
  { name: "Niagara Falls", kind: "landmark", match: /\bniagara falls\b/i, bbox: [-79.090, 43.075, -79.060, 43.092], preposition: "by" },
  { name: "Old Montreal", kind: "district", match: /\b(old montreal|vieux-?montr[ée]al)\b/i, bbox: [-73.562, 45.499, -73.543, 45.514], preposition: "through" },
  { name: "Banff & the Bow River", kind: "park", match: /\b(banff|bow river)\b/i, bbox: [-115.65, 51.10, -115.45, 51.25], preposition: "through" },

  // ---------- Iconic landmarks — Europe ----------
  { name: "the Eiffel Tower", kind: "landmark", match: /\b(eiffel tower|tour eiffel)\b/i, bbox: [2.292, 48.856, 2.298, 48.860], preposition: "by" },
  { name: "the Seine", kind: "district", match: /\b(seine)\b/i, bbox: [2.270, 48.830, 2.405, 48.905], preposition: "along" },
  { name: "Montmartre", kind: "district", match: /\bmontmartre\b/i, bbox: [2.336, 48.882, 2.348, 48.892], preposition: "through" },
  { name: "the Colosseum", kind: "landmark", match: /\b(colosseum|colosseo)\b/i, bbox: [12.490, 41.888, 12.495, 41.892], preposition: "by" },
  { name: "the Trevi Fountain", kind: "landmark", match: /\btrevi (fountain|fontana)\b/i, bbox: [12.481, 41.900, 12.484, 41.902], preposition: "by" },
  { name: "the Amalfi Coast", kind: "coast", match: /\b(amalfi coast|costiera amalfitana|sr ?163)\b/i, bbox: [14.40, 40.575, 14.75, 40.665], preposition: "along" },
  { name: "Florence's historic center", kind: "district", match: /\b(florence|firenze) (historic|centro storico)\b/i, bbox: [11.246, 43.766, 11.265, 43.778], preposition: "through" },
  { name: "the Grand Canal", kind: "district", match: /\b(grand canal|canal grande)\b/i, bbox: [12.318, 45.430, 12.345, 45.444], preposition: "along" },
  { name: "the Sagrada Família", kind: "landmark", match: /\bsagrada fam[íi]lia\b/i, bbox: [2.172, 41.402, 2.176, 41.406], preposition: "by" },
  { name: "Park Güell", kind: "park", match: /\bpark g[üu]ell\b/i, bbox: [2.150, 41.412, 2.156, 41.417], preposition: "by" },
  { name: "the Gothic Quarter", kind: "district", match: /\b(gothic quarter|barri g[òo]tic)\b/i, bbox: [2.172, 41.379, 2.182, 41.386], preposition: "through" },
  { name: "Tower Bridge", kind: "landmark", match: /\btower bridge\b/i, bbox: [-0.078, 51.504, -0.072, 51.508], preposition: "across" },
  { name: "Westminster", kind: "landmark", match: /\b(westminster|houses of parliament|big ben)\b/i, bbox: [-0.131, 51.498, -0.122, 51.503], preposition: "by" },
  { name: "the Thames Embankment", kind: "district", match: /\bthames( embankment)?\b/i, bbox: [-0.135, 51.504, -0.095, 51.515], preposition: "along" },
  { name: "Notting Hill", kind: "district", match: /\bnotting hill\b/i, bbox: [-0.218, 51.504, -0.193, 51.522], preposition: "through" },
  { name: "the Royal Crescent", kind: "landmark", match: /\broyal crescent\b/i, bbox: [-2.368, 51.385, -2.360, 51.388], preposition: "by" },
  { name: "the Royal Mile", kind: "street", match: /\broyal mile\b/i, bbox: [-3.198, 55.948, -3.173, 55.953], preposition: "along" },
  { name: "the Amsterdam canals", kind: "district", match: /\b(amsterdam canals|grachtengordel|herengracht|prinsengracht|keizersgracht)\b/i, bbox: [4.878, 52.362, 4.912, 52.388], preposition: "along" },
  { name: "Lake Geneva", kind: "lake", match: /\b(lake geneva|lac l[ée]man)\b/i, bbox: [6.10, 46.20, 6.95, 46.55], preposition: "along" },
  { name: "Lauterbrunnen Valley", kind: "park", match: /\blauterbrunnen\b/i, bbox: [7.878, 46.555, 7.945, 46.625], preposition: "through" },

  // ---------- Iconic landmarks — Asia ----------
  { name: "Shibuya Crossing", kind: "landmark", match: /\bshibuya (crossing|scramble)\b/i, bbox: [139.699, 35.658, 139.703, 35.660], preposition: "by" },
  { name: "Meiji Shrine & Yoyogi Park", kind: "park", match: /\b(meiji (shrine|jingu)|yoyogi park)\b/i, bbox: [139.690, 35.665, 139.708, 35.685], preposition: "by" },
  { name: "Shinjuku Gyoen", kind: "park", match: /\bshinjuku gyoen\b/i, bbox: [139.705, 35.681, 139.715, 35.690], preposition: "by" },
  { name: "the Arashiyama Bamboo Grove", kind: "forest", match: /\barashiyama( bamboo)?\b/i, bbox: [135.665, 35.013, 135.678, 35.020], preposition: "through" },
  { name: "the Gion District", kind: "district", match: /\bgion\b/i, bbox: [135.770, 35.001, 135.780, 35.006], preposition: "through" },
  { name: "the Bund", kind: "district", match: /\bthe bund|外滩|wàitān\b/i, bbox: [121.485, 31.235, 121.495, 31.250], preposition: "along" },
  { name: "West Lake", kind: "lake", match: /\bwest lake|西湖\b/i, bbox: [120.130, 30.235, 120.165, 30.265], preposition: "by" },
  { name: "the Great Wall", kind: "landmark", match: /\bgreat wall|长城\b/i, bbox: [116.55, 40.30, 116.65, 40.50], preposition: "by" },
  { name: "Victoria Harbour", kind: "coast", match: /\bvictoria harbour\b/i, bbox: [114.155, 22.275, 114.195, 22.305], preposition: "along" },
  { name: "Marina Bay", kind: "district", match: /\bmarina bay\b/i, bbox: [103.851, 1.279, 103.865, 1.290], preposition: "by" },
  { name: "Gardens by the Bay", kind: "park", match: /\bgardens by the bay\b/i, bbox: [103.860, 1.278, 103.870, 1.286], preposition: "by" },

  // ---------- Iconic landmarks — Oceania ----------
  { name: "the Sydney Opera House", kind: "landmark", match: /\bsydney opera house\b/i, bbox: [151.213, -33.858, 151.217, -33.854], preposition: "by" },
  { name: "the Sydney Harbour Bridge", kind: "landmark", match: /\bsydney harbour bridge\b/i, bbox: [151.207, -33.855, 151.214, -33.848], preposition: "across" },
  { name: "the Bondi to Coogee coast", kind: "coast", match: /\b(bondi|coogee|bronte)\b/i, bbox: [151.255, -33.928, 151.275, -33.890], preposition: "along" },
  { name: "Queenstown lakefront", kind: "lake", match: /\bqueenstown\b/i, bbox: [168.652, -45.040, 168.680, -45.025], preposition: "along" },
  { name: "Milford Sound", kind: "coast", match: /\bmilford sound\b/i, bbox: [167.85, -44.70, 167.95, -44.60], preposition: "along" },

  // ---------- Iconic landmarks — South America ----------
  { name: "Christ the Redeemer", kind: "landmark", match: /\b(christ the redeemer|cristo redentor)\b/i, bbox: [-43.213, -22.953, -43.208, -22.949], preposition: "by" },
  { name: "Sugarloaf Mountain", kind: "mountain", match: /\b(sugarloaf|p[ãa]o de a[çc][úu]car)\b/i, bbox: [-43.166, -22.954, -43.154, -22.945], preposition: "by" },
  { name: "Copacabana & Ipanema", kind: "coast", match: /\b(copacabana|ipanema|leblon)\b/i, bbox: [-43.222, -22.991, -43.180, -22.965], preposition: "along" },
  { name: "the Palermo parks", kind: "park", match: /\bpalermo\b/i, bbox: [-58.422, -34.586, -58.385, -34.560], preposition: "through" },
  { name: "Recoleta", kind: "district", match: /\brecoleta\b/i, bbox: [-58.402, -34.595, -58.378, -34.580], preposition: "through" },

  // ---------- Iconic landmarks — Middle East & Africa ----------
  { name: "the Burj Khalifa", kind: "landmark", match: /\bburj khalifa\b/i, bbox: [55.272, 25.196, 55.276, 25.200], preposition: "by" },
  { name: "Palm Jumeirah", kind: "district", match: /\bpalm jumeirah\b/i, bbox: [55.105, 25.075, 55.155, 25.135], preposition: "across" },
  { name: "Petra", kind: "landmark", match: /\bpetra\b/i, bbox: [35.435, 30.318, 35.450, 30.332], preposition: "by" },
  { name: "Table Mountain", kind: "mountain", match: /\btable mountain\b/i, bbox: [18.395, -33.985, 18.420, -33.945], preposition: "by" },
  { name: "the V&A Waterfront", kind: "district", match: /\bv\s*&\s*a waterfront\b/i, bbox: [18.418, -33.910, 18.426, -33.900], preposition: "by" },
  { name: "the Pyramids of Giza", kind: "landmark", match: /\b(pyramids of giza|great pyramid)\b/i, bbox: [31.128, 29.972, 31.145, 29.985], preposition: "by" },
];

const inBbox = (p: LngLat, b: [number, number, number, number]) =>
  p[0] >= b[0] && p[0] <= b[2] && p[1] >= b[1] && p[1] <= b[3];

/**
 * Find iconic landmarks the route either crosses (geometry) or passes (named
 * features). Returns at most `limit` distinct icons in stable registry order.
 */
export function findIconicLandmarks(
  coords: LngLat[] | undefined,
  names: ReadonlyArray<string | undefined> = [],
  limit = 2,
): IconicLandmark[] {
  const found = new Set<string>();
  const out: IconicLandmark[] = [];
  const flatNames = names.filter((n): n is string => !!n && n.trim().length > 0);

  for (const icon of ICONIC_LANDMARKS) {
    if (out.length >= limit) break;
    if (found.has(icon.name)) continue;

    // 1) Name match — fast path against whatever the corridor scan found.
    const nameHit = flatNames.some((n) => icon.match.test(n));

    // 2) Geometry match — sample every 8th coord to keep it cheap on long
    //    polylines while still catching short crossings reliably.
    let geoHit = false;
    if (!nameHit && icon.bbox && coords && coords.length > 0) {
      const step = Math.max(1, Math.floor(coords.length / 200));
      for (let i = 0; i < coords.length; i += step) {
        if (inBbox(coords[i], icon.bbox)) {
          geoHit = true;
          break;
        }
      }
    }

    if (nameHit || geoHit) {
      found.add(icon.name);
      out.push(icon);
    }
  }
  return out;
}
